import { NavigateFunction } from 'react-router-dom';
import {
  DEFAULT_LANGUAGE,
  LANGUAGE_LIST,
  QuestionResultType,
} from 'src/contstant/commonLabels';
import routePaths from 'src/contstant/routePaths';
import { MediaData } from 'src/types/helios/Media';
import {
  MediaResponse,
  Question,
  QuestionContent,
  SubQuestion,
} from 'src/types/helios/Question';
import {
  replaceMediaIdsWithPresignedUrlsForQuestion,
  replaceMediaIdsWithPresignedUrlsForQuestionSubQuestion,
} from 'src/utils/mediaUtils';
import { isImageExtension } from 'src/contstant/fileConstants';
import { FileMetadata } from 'src/components/FileDisplay';
import { QuestionWithResponse } from 'src/types/helios/Audit';
import {
  backfillErtFields,
  backfillScoredRequirementFlags,
} from 'src/v2/shared/domain/subQuestionCanonicalFields';

/**
 * Helper functions for handling question-related operations including
 * content localization, navigation, and media URL processing
 */

/**
 * Gets the priority value for a question result for sorting purposes
 * Priority order: FAIL (0), PASS (1), NA (2), others (3)
 * @param result - The question result string
 * @returns Priority number (lower = higher priority)
 */
export function getQuestionResultPriority(result: string | undefined): number {
  if (!result) return 3;
  const resultLower = result.toLowerCase();
  if (resultLower === QuestionResultType.FAIL.toLowerCase()) return 0;
  if (resultLower === QuestionResultType.PASS.toLowerCase()) return 1;
  if (resultLower === QuestionResultType.NA.toLowerCase()) return 2;
  return 3;
}

/**
 * Sorts questions by result priority: Failed first, then Pass, then N/A, then others
 * Creates a new sorted array without mutating the original
 * @param questions - Array of questions to sort
 * @returns New sorted array of questions
 */
export function sortQuestionsByResult(
  questions: QuestionWithResponse[],
): QuestionWithResponse[] {
  return [...questions].sort((a, b) => {
    const aResult = a.responseDetails?.mainQuestionResponse?.responseResult;
    const bResult = b.responseDetails?.mainQuestionResponse?.responseResult;

    const aPriority = getQuestionResultPriority(aResult);
    const bPriority = getQuestionResultPriority(bResult);

    return aPriority - bPriority;
  });
}

/**
 * Generates a consistent field key for subquestion document storage
 * @param index - The subquestion index
 * @param field - The field name ('instruction' or 'taskDescription')
 * @returns Formatted field key string
 */
export function getSubQuestionFieldKey(
  index: number,
  field: 'instruction' | 'taskDescription',
): string {
  return `subQuestion_${index}_${field}`;
}

/**
 * Checks if a file extension represents an image file
 * Images are embedded in content, so they're filtered out from document lists
 * @param extension - File extension to check (with or without dot)
 * @returns True if the extension is an image type
 */
export function isImageFile(extension: string | undefined): boolean {
  if (!extension) return false;
  return isImageExtension(extension);
}

/**
 * Copies structural fields from the English sub-question onto a translated
 * copy. Translations carry text only (title, instruction, task text), so
 * responseType, options, showWhenParentIs, require*, media ids and the
 * sub-question id come from English. A field the translation carries wins;
 * null counts as missing.
 * Without this, a translated TEXT sub renders as Pass/Fail/NA (P547193080).
 */
function overlayCanonicalSubQuestionFields(
  translated: SubQuestion,
  canonical: SubQuestion | undefined,
): SubQuestion {
  if (!canonical) {
    return translated;
  }
  return {
    ...translated,
    ...(translated.subQuestionId == null && canonical.subQuestionId != null
      ? { subQuestionId: canonical.subQuestionId }
      : {}),
    ...(translated.mediaIds == null && canonical.mediaIds != null
      ? { mediaIds: canonical.mediaIds }
      : {}),
    ...(translated.mediaIdsMap == null && canonical.mediaIdsMap != null
      ? { mediaIdsMap: canonical.mediaIdsMap }
      : {}),
    ...backfillScoredRequirementFlags(translated, canonical),
    ...backfillErtFields(translated, canonical),
  };
}

function indexSubQuestionsById(
  subQuestions: SubQuestion[] | undefined,
): Map<string, SubQuestion> {
  const byId = new Map<string, SubQuestion>();
  for (const sq of subQuestions ?? []) {
    if (sq.subQuestionId) {
      byId.set(sq.subQuestionId, sq);
    }
  }
  return byId;
}

/**
 * Retrieves localized question content based on selected language
 * Falls back to default content if translation is not available
 * @param question - The question object containing content and translations
 * @param selectedLanguage - The language code for desired translation
 * @param error - Optional error callback function
 * @returns QuestionContent object with appropriate language content
 */
export const getQuestionContent = (
  question: Question,
  selectedLanguage?: string,
  error?: (error: string) => void,
): QuestionContent => {
  // Clear any existing error messages
  if (error) {
    error('');
  }

  // Set up default content fallback
  const defaultContent = question?.localizedContent?.content || {
    ...question.defaultLanguageContent,
    subQuestions: question.subQuestions,
  };

  // Return default content if no language is selected
  if (!selectedLanguage) {
    return defaultContent;
  }

  // Check for translations in localizedContent structure
  if (
    question?.localizedContent?.translations &&
    Object.entries(question.localizedContent.translations).length > 0 &&
    selectedLanguage
  ) {
    const translationEntry = question.localizedContent.translations[selectedLanguage];
    const translatedContent = translationEntry?.content;
    if (!translatedContent) {
      return defaultContent;
    }
    if (!translatedContent.subQuestions) {
      return translatedContent;
    }
    const canonicalById = indexSubQuestionsById(
      defaultContent.subQuestions ?? question.subQuestions,
    );
    return {
      ...translatedContent,
      subQuestions: translatedContent.subQuestions.map((sq) =>
        overlayCanonicalSubQuestionFields(
          sq,
          sq.subQuestionId ? canonicalById.get(sq.subQuestionId) : undefined,
        ),
      ),
    };
  }

  // Check for translations in localeTranslationContentMap structure
  if (
    question?.localeTranslationContentMap &&
    Object.entries(question?.localeTranslationContentMap).length > 0
  ) {
    const translatedContent = question?.localeTranslationContentMap[selectedLanguage];
    if (translatedContent) {
      // Build translated content object with all required fields
      const content: QuestionContent = {
        questionDetails: {
          title: translatedContent?.questionTranslation?.questionDetails?.title || '',
          questionText:
            translatedContent?.questionTranslation?.questionDetails?.questionText || '',
          auditInstruction:
            translatedContent?.questionTranslation?.questionDetails?.auditInstruction ||
            '',
          passCriteria:
            translatedContent?.questionTranslation?.questionDetails?.passCriteria || '',
          failCriteria:
            translatedContent?.questionTranslation?.questionDetails?.failCriteria || '',
          justification:
            translatedContent?.questionTranslation?.questionDetails?.justification || '',
        },
        taskDetails: {
          taskId: translatedContent?.questionTranslation?.taskDetails?.taskId || '',
          taskName: translatedContent?.questionTranslation?.taskDetails?.taskName || '',
          taskDescription:
            translatedContent?.questionTranslation?.taskDetails?.taskDescription || '',
          taskDurationDays:
            translatedContent?.questionTranslation?.taskDetails?.taskDurationDays || 0,
        },
        // Map subquestions with their translations while preserving order.
        // The map holds text only; structure comes from the English item.
        subQuestions:
          question.subQuestions
            ?.map((item) =>
              overlayCanonicalSubQuestionFields(
                {
                  ...translatedContent?.subQuestionsTranslationMap?.[
                    item.subQuestionId || ''
                  ],
                  orderIndex: item.orderIndex,
                },
                item,
              ),
            )
            .filter(Boolean) || [],
      };
      return content;
    } else {
      // Handle missing translation for non-default languages
      if (error && selectedLanguage !== DEFAULT_LANGUAGE) {
        error(
          `No translation available for ${LANGUAGE_LIST.find((item) => item.value === selectedLanguage)?.label} (${selectedLanguage}).`,
        );
      }
      return defaultContent;
    }
  } else {
    // Handle case where no translation map exists
    if (error && selectedLanguage !== DEFAULT_LANGUAGE) {
      error(
        `No translation available for ${LANGUAGE_LIST.find((item) => item.value === selectedLanguage)?.label} (${selectedLanguage}).`,
      );
    }
  }

  return defaultContent;
};

export function getQuestionDetailsUrl(data: Question) {
  const params = new URLSearchParams({
    programId: data.program,
    questionId: data.questionId,
    division: data.divisionName,
    businessUnit: data.businessUnit,
  });
  return `${routePaths.QUESTION_DETAILS.path}?${params}`;
}

/**
 * Navigates to the question details page with proper URL parameters
 * @param navigate - React Router navigation function
 * @param data - Question object containing navigation parameters
 * @param isReplace - Whether to replace current history entry
 * @param openNewTab - Whether to open in a new browser tab
 */
export function openQuestionDetailsPage(
  navigate: NavigateFunction,
  data: Question,
  isReplace?: boolean,
  openNewTab?: boolean,
) {
  if (data) {
    const url = getQuestionDetailsUrl(data);
    // Handle different navigation modes
    if (openNewTab === true) {
      window.open(url, '_blank');
    } else {
      if (isReplace === true) {
        navigate(url, { replace: true });
      } else {
        navigate(url);
      }
    }
  }
}

/**
 * Navigates to the question edit page with question data in state
 * @param navigate - React Router navigation function
 * @param isCopy - Whether this is a copy operation
 * @param data - Optional question data to edit
 */
export function openQuestionEditPage(
  navigate: NavigateFunction,
  isCopy: boolean,
  data?: Question,
) {
  // Pass question data and copy flag through navigation state
  navigate(routePaths.QUESTION_EDIT.path, {
    state: {
      question: data,
      copy: isCopy,
    },
  });
}

/**
 * Processes and replaces media IDs with presigned URLs throughout question content
 * @param data - Question object containing media references
 * @returns Question object with media IDs replaced by presigned URLs
 */
export function processQuestionMediaUrls(data: Question): Question {
  // Process question details fields that may contain media references
  const questionFields = [
    'auditInstruction',
    'failCriteria',
    'passCriteria',
    'justification',
    'questionText',
  ] as const;

  // Replace media IDs with presigned URLs in each field
  questionFields.forEach((field) => {
    if (data.defaultLanguageContent.questionDetails[field]) {
      data.defaultLanguageContent.questionDetails[field] =
        replaceMediaIdsWithPresignedUrlsForQuestion(
          data.defaultLanguageContent.questionDetails[field],
          data.mediaResponses,
        );
    }
  });

  // Process task description field for media references
  if (data.defaultLanguageContent?.taskDetails?.taskDescription) {
    data.defaultLanguageContent.taskDetails.taskDescription =
      replaceMediaIdsWithPresignedUrlsForQuestion(
        data.defaultLanguageContent.taskDetails.taskDescription,
        data.mediaResponses,
      );
  }

  // Process subquestions for media references
  if (data.subQuestions) {
    data.subQuestions = data.subQuestions.map((subQuestion) => {
      // Replace media IDs in subquestion instruction
      if (subQuestion.instruction) {
        subQuestion.instruction = replaceMediaIdsWithPresignedUrlsForQuestionSubQuestion(
          subQuestion.instruction,
          subQuestion.mediaResponses,
        );
      }

      // Replace media IDs in subquestion task description
      if (subQuestion.taskDetails) {
        subQuestion.taskDetails.taskDescription =
          replaceMediaIdsWithPresignedUrlsForQuestionSubQuestion(
            subQuestion.taskDetails.taskDescription,
            subQuestion.mediaResponses,
          );
      }

      return subQuestion;
    });
  }
  return data;
}

/**
 * Extracts all media responses from a question and its subquestions
 * @param question - Question object to extract media from
 * @returns Array of MediaData objects containing media information and presigned URLs
 */
export function getAlLMediaResponseList(question: Question): MediaData[] {
  const list: MediaData[] = [];

  try {
    // Return empty list if no question provided
    if (!question) {
      return list;
    }

    // Extract media responses from main question level
    if (question.mediaResponses && typeof question.mediaResponses === 'object') {
      Object.values(question.mediaResponses).forEach((responses) => {
        if (responses && Array.isArray(responses)) {
          // Filter out null responses and transform to MediaData format
          list.push(
            ...responses
              .filter((r) => r != null)
              .map((r) => ({
                media: {
                  mediaId: r.media.mediaId,
                  entityType: 'QUESTION' as any,
                  s3Key: '',
                  fileName: '',
                  extension: '',
                  fileSize: 0,
                  createdBy: '',
                },
                presignedUrl: r.presignedUrl,
              })),
          );
        }
      });
    }

    // Extract media responses from all subquestions
    if (question.subQuestions && Array.isArray(question.subQuestions)) {
      question.subQuestions.forEach((subQuestion) => {
        if (subQuestion?.mediaResponses && Array.isArray(subQuestion.mediaResponses)) {
          // Filter out null responses and transform to MediaData format
          list.push(
            ...subQuestion.mediaResponses
              .filter((r) => r != null)
              .map((r) => ({
                media: {
                  mediaId: r.media.mediaId,
                  entityType: 'QUESTION' as any,
                  s3Key: '',
                  fileName: '',
                  extension: '',
                  fileSize: 0,
                  createdBy: '',
                },
                presignedUrl: r.presignedUrl,
              })),
          );
        }
      });
    }
  } catch (error) {
    // Log any errors during media processing but don't throw
    console.error('Error processing media responses:', error);
  }

  return list;
}

/**
 * Extracts document metadata from question mediaResponses for TextEditor in readonly mode
 * Filters out embedded images and only returns documents
 * @param question - Question object containing mediaResponses
 * @param fieldName - The field name to extract documents for (e.g., 'questionText', 'auditInstruction', 'taskDescription')
 * @returns Array of FileMetadata objects for documents
 */
export function getDocumentMetadata(
  question: Question | null,
  fieldName: keyof Question['mediaResponses'],
): Array<{ fileName: string; mediaId: string; presignedUrl: string }> {
  if (!question?.mediaResponses) return [];

  const mediaResponsesList = question.mediaResponses[fieldName];
  if (!mediaResponsesList) return [];

  // Filter out embedded images - only get non-image documents
  const documentMedia = mediaResponsesList.filter((mediaResponse: MediaResponse) => {
    // Check if it's an image extension
    const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(
      mediaResponse.media.extension?.toLowerCase() || '',
    );
    // Only include non-images, exclude all images (embedded or not)
    return !isImage;
  });

  // Extract metadata only - no file fetching
  return documentMedia.map((mediaResponse: MediaResponse) => ({
    fileName: mediaResponse.media.fileName || 'document',
    mediaId: mediaResponse.media.mediaId,
    presignedUrl: mediaResponse.presignedUrl,
  }));
}

/**
 * Load existing document attachments from mediaResponses as metadata
 * Separates embedded images from document attachments for all fields
 * @param question - Question object containing mediaResponses
 * @returns Record of field names to their document metadata arrays
 */
export function loadExistingDocuments(
  question: Question | null,
): Record<string, FileMetadata[]> {
  if (!question?.mediaResponses) return {};

  const documentMetadataMap: Record<string, FileMetadata[]> = {};

  // Process each field's media responses
  for (const [fieldName, mediaResponsesList] of Object.entries(question.mediaResponses)) {
    // Filter out all images - only get non-image documents
    const documentMedia = mediaResponsesList.filter((mediaResponse: MediaResponse) => {
      // Check if it's an image extension
      const isImage = isImageFile(mediaResponse.media.extension);
      // Only include non-images, exclude all images (embedded or not)
      return !isImage;
    });

    if (documentMedia.length > 0) {
      // Extract metadata only - no file fetching
      const metadata: FileMetadata[] = documentMedia.map(
        (mediaResponse: MediaResponse) => ({
          fileName: mediaResponse.media.fileName || 'document',
          mediaId: mediaResponse.media.mediaId,
          presignedUrl: mediaResponse.presignedUrl,
        }),
      );

      documentMetadataMap[fieldName] = metadata;
    }
  }

  // Process subquestion documents
  if (question.subQuestions) {
    question.subQuestions.forEach((subQuestion, index) => {
      // Check if mediaResponsesMap exists (new structure)
      if (subQuestion.mediaResponsesMap) {
        // Process instruction field
        const instructionMediaResponses = subQuestion.mediaResponsesMap.instruction || [];

        const instructionMedia = instructionMediaResponses.filter(
          (mediaResponse: MediaResponse) => {
            return !isImageFile(mediaResponse.media.extension);
          },
        );

        if (instructionMedia.length > 0) {
          const instructionFieldKey = getSubQuestionFieldKey(index, 'instruction');
          documentMetadataMap[instructionFieldKey] = instructionMedia.map(
            (mediaResponse: MediaResponse) => ({
              fileName: mediaResponse.media.fileName || 'document',
              mediaId: mediaResponse.media.mediaId,
              presignedUrl: mediaResponse.presignedUrl,
            }),
          );
        }

        // Process taskDescription field
        const taskDescriptionMediaResponses =
          subQuestion.mediaResponsesMap.taskDescription || [];

        const taskDescriptionMedia = taskDescriptionMediaResponses.filter(
          (mediaResponse: MediaResponse) => {
            return !isImageFile(mediaResponse.media.extension);
          },
        );

        if (taskDescriptionMedia.length > 0) {
          const taskDescriptionFieldKey = getSubQuestionFieldKey(
            index,
            'taskDescription',
          );
          documentMetadataMap[taskDescriptionFieldKey] = taskDescriptionMedia.map(
            (mediaResponse: MediaResponse) => ({
              fileName: mediaResponse.media.fileName || 'document',
              mediaId: mediaResponse.media.mediaId,
              presignedUrl: mediaResponse.presignedUrl,
            }),
          );
        }
      }
    });
  }

  return documentMetadataMap;
}

/**
 * Extracts document metadata from subquestion mediaResponsesMap for TextEditor in readonly mode
 * Filters out embedded images and only returns documents
 * @param subQuestion - SubQuestion object containing mediaResponsesMap
 * @param fieldName - The field name to extract documents for ('instruction' or 'taskDescription')
 * @returns Array of FileMetadata objects for documents
 */
export function getSubQuestionDocumentMetadata(
  subQuestion: SubQuestion,
  fieldName: 'instruction' | 'taskDescription',
): FileMetadata[] {
  if (!subQuestion?.mediaResponsesMap) return [];

  const mediaResponsesList = subQuestion.mediaResponsesMap[fieldName];
  if (!mediaResponsesList || !Array.isArray(mediaResponsesList)) return [];

  // Filter out embedded images - only get non-image documents
  const documentMedia = mediaResponsesList.filter((mediaResponse: MediaResponse) => {
    // Only include non-images, exclude all images (embedded or not)
    return !isImageFile(mediaResponse.media.extension);
  });

  // Extract metadata only
  return documentMedia.map((mediaResponse: MediaResponse) => ({
    fileName: mediaResponse.media.fileName || 'document',
    mediaId: mediaResponse.media.mediaId,
    presignedUrl: mediaResponse.presignedUrl,
  }));
}
